import useScrollReset from "@hooks/ScrollHook";
import { NftTabs } from "@components/tabs/NftTabs";
import { Link, useNavigate, useParams } from "react-router-dom";
import { errorPromise, useApi } from "@hooks/ApiHook";
import ActionDropdown from "@components/dropdown/ActionDropdown";
import { TraitsTab } from "@app/item/tabs/TraitsTab";
import { SelectableCoinBadge } from "@components/SelectableCoinBadge";
import { useEffect, useState } from "react";
import { useAccount } from "@contexts/AccountContext";
import useAlert from "@hooks/AlertHook";
import { useWasmX } from "@hooks/WasmXHook";
import { useSelectedChain } from "@app/examples/ChainSelector";
import { useCoins } from "@contexts/CoinsContext";
import { TxInfo } from "@terra-money/terra.js";
import { useTxModal } from "@hooks/TxModalHook";

export function MintableItemPage() {
    useScrollReset()
    const { id } = useParams()
    const navigate = useNavigate();
    const { address } = useAccount()
    const alert = useAlert();
    const wasmX = useWasmX();
    const { selectedChain } = useSelectedChain()
    const { getCoin } = useCoins();
    const txModal = useTxModal()

    const [amount, setAmount] = useState(1)
    const [payment, setPayment] = useState<string>()

    const { data: item } = useApi(api => api.item.find(id!))
    const { data: supply } = useApi(api => api.item.itemSupply(id!))
    const { data: traits } = useApi(api => api.item.itemTraits(id!))
    const { data: collection } = useApi(api => {
        if (!item) return errorPromise()
        return api.collection.find(item.collectionId)
    }, [item])
    const { data: mintableDetail } = useApi(api => api.item.listDetail(id!))

    useEffect(() => {
        if (address && collection?.ownerAddress === address) navigate(`/item/${id}`, { replace: true })
    }, [collection, address])

    const onMint = async () => {
        try {
            if (!supply) throw new Error("Cannot load supply info")
            if (!payment) throw new Error("Please select payment type")
            if (!wasmX || !selectedChain) throw new Error("Cannot read chain information")
            if (!address) throw new Error("Cannot load user's information, try to refresh the page")
            if (!mintableDetail || !item || !collection) throw new Error("Cannot load item's information, try to refresh the page")
            if (amount > supply.available) throw new Error(`Max amount to mint is ${supply.available}`)
            if (amount < 1) throw new Error(`Min amount is 1`)

            const mintPrice = (mintableDetail.prices!.find(e => e.coinId == payment)!.price * amount).toString();
            const coin = getCoin(payment)!;
            const asset = coin.isCw20 ? { token: { address: coin.cw20Address!, amount: mintPrice } } : { coin: { amount: mintPrice, denom: coin.denom! } };
            const message = wasmX.collection.execute.mint({
                contract: collection.collectionContract,
                sender: address,
                data: {
                    owner: address,
                    amount: amount,
                    voucher: mintableDetail.voucher,
                    extension: {
                        animation_url: item.animationUrl,
                        attributes: traits?.map(trait => ({ trait_type: trait.traitType!, value: trait.value!, display_type: trait.displayType })),
                        background_color: item.backgroundColor,
                        description: item.description,
                        external_url: item.externalUrl,
                        identifier: item.id,
                        image: item.image,
                        image_data: item.imageData,
                        name: item.name,
                        supply: item.supply,
                        token_uri: item.tokenUri,
                        youtube_url: item.youtubeUrl
                    },
                    prices: mintableDetail.prices!.map(e => {
                        const { isCw20, denom, cw20Address } = getCoin(e.coinId)!
                        if (isCw20) return { token: { amount: e.price.toString(), address: cw20Address! } }
                        else return { coin: { amount: e.price.toString(), denom: denom! } }
                    })
                }
            }, asset)

            await new Promise<TxInfo>((onSuccess, onError) => txModal.show(address!, [message], `Mint '${item.name}'`, {
                onSuccess,
                onError
            }))

            navigate("/profile")
            alert.success("Item minted successfully")

        } catch (e) {
            console.error(JSON.stringify(e))
            if (e instanceof Error) alert.failed(e.message)
            else throw e;
        }
    }

    return <main className="mt-24">
        <section className="relative pt-12 pb-24 lg:py-24">
            <picture className="pointer-events-none absolute inset-0 -z-10 dark:hidden">
                <img src="/src/img/gradient_light.jpg" alt="gradient" className="h-full w-full" />
            </picture>
            <div className="container">
                <div className="md:flex md:flex-wrap">
                    <figure className="mb-8 md:w-2/5 md:flex-shrink-0 md:flex-grow-0 md:basis-auto lg:w-1/2">
                        <img
                            src={item?.image}
                            alt="item"
                            className="cursor-pointer w-full rounded-2.5xl"
                        />
                    </figure>

                    <div className="md:w-3/5 md:basis-auto md:pl-8 lg:w-1/2 lg:pl-[3.75rem]">
                        <div className="mb-3 flex">
                            <div className="flex items-center">
                                <Link to={`/collection/${item?.collection?.slug}`} className="mr-2 text-lg font-bold text-jacarta-500">{item?.collection?.name}</Link>
                            </div>
                        </div>

                        <h1 className="mb-4 font-display text-4xl font-semibold text-jacarta-700 dark:text-white">{item?.name}</h1>

                        <p className="mb-5 dark:text-gray-400">
                            {item?.description}
                        </p>

                        <div className="mb-2 flex flex-wrap">
                            <Link to={`/user/${item?.collection?.owner?.username}`} className="mr-8 mb-4 flex">
                                <figure className="mr-4 shrink-0">
                                    <div className="relative block">
                                        <img src={item?.collection?.owner?.thumbUrl ?? "/src/img/logo/transparant_logo.png"} alt="avatar 7" className="rounded-2lg w-12" loading="lazy" />
                                    </div>
                                </figure>
                                <div className="flex flex-col justify-center">
                                    <span className="block text-sm text-jacarta-400 dark:text-white">
                                        Creator
                                    </span>
                                    <div className="block text-jacarta-400">
                                        <span className="text-sm font-bold">{item?.collection?.owner?.username}</span>
                                    </div>
                                </div>
                            </Link>
                        </div>

                        {
                            mintableDetail && <div
                                className="rounded-2lg border border-jacarta-100 bg-white p-8 dark:border-gray-400 dark:bg-gray-900"
                            >

                                <div className="flex flex-wrap">
                                    {
                                        mintableDetail.prices?.map(price => <div className="mr-2.5 mb-2.5" key={price.coinId}>
                                            <SelectableCoinBadge
                                                onSelected={coinId => setPayment(coinId === payment ? undefined : coinId)}
                                                isSelected={price.coinId == payment}
                                                amount={price.price}
                                                coinId={price.coinId} />
                                        </div>
                                        )
                                    }
                                </div>

                                <div className="mb-5">
                                    <label htmlFor="item-supply"
                                        className="block font-display text-jacarta-700 dark:text-white">Amount</label>
                                    <p className="mb-3 text-2xs dark:text-gray-400">
                                        Available ({supply?.available}/{supply?.total})
                                    </p>
                                    <input
                                        value={amount}
                                        onChange={({ target }) => setAmount(target.valueAsNumber)}
                                        type="number"
                                        id="item-supply"
                                        className="w-full rounded-lg border-jacarta-100 py-3 hover:ring-2 hover:ring-jacarta-500/10 focus:ring-jacarta-500 dark:border-gray-400 dark:bg-gray-900 dark:text-white dark:placeholder:text-gray-400"
                                        placeholder="Amount"
                                        required />
                                </div>


                                <button
                                    onClick={onMint}
                                    className="inline-block w-full rounded-full bg-jacarta-500 py-3 px-8 text-center font-semibold text-white transition-all hover:bg-jacarta-600">
                                    Mint
                                </button>
                            </div>
                        }
                    </div>
                </div>

                <div className="scrollbar-custom mt-14 overflow-x-auto rounded-lg">
                    <div className="min-w-fit">
                        <NftTabs
                            items={{
                                "Traits": traits && <TraitsTab traits={traits} />
                            }}
                        />
                    </div>
                </div>
            </div>
        </section>
    </main>
}